// Admin-configured, user-facing texts are stored either as a plain string
// (single language, the historical shape) or as a language-keyed map like the
// flow blocks' TextToSend. Both shapes stay valid indefinitely: blocks live in
// per-tenant, revision-pinned collections, so there is no single point at which
// every stored value can be converted.
export type TLocalizedText = string | Record<string, string>;

// Falls back in this order: the requested language, the bot's default language,
// then any filled translation. An admin who leaves a language blank gets the
// default language's copy rather than an empty label, and a value stored under
// an unexpected key still renders something.
export const resolveLocalizedText = (
  text: TLocalizedText | undefined,
  language: string,
  defaultLanguage?: string,
): string => {
  if (typeof text === 'string') {
    return text;
  }

  if (!text) {
    return '';
  }

  const translationForLanguage = text[language];
  if (translationForLanguage) {
    return translationForLanguage;
  }

  const translationForDefaultLanguage = defaultLanguage
    ? text[defaultLanguage]
    : '';
  if (translationForDefaultLanguage) {
    return translationForDefaultLanguage;
  }

  const firstFilledTranslation = Object.values(text).find(
    (translation) => !!translation,
  );

  return firstFilledTranslation || '';
};

// Prepares an admin form's default values: gives every configured language a
// defined value so each rendered field submits a string rather than undefined,
// and files a legacy plain string under the default language so saving the form
// converts it. Companies with no configured language keep the plain string.
export const toLocalizedText = (
  text: TLocalizedText | undefined,
  languages: string[],
  defaultLanguage: string,
): TLocalizedText => {
  if (!languages.length) {
    return text ?? '';
  }

  const legacyTextLanguage = defaultLanguage || languages[0];
  const translations: Record<string, string> =
    typeof text === 'object'
      ? { ...text }
      : { [legacyTextLanguage]: text ?? '' };

  // A value can sit under a language the company does not have configured - the
  // menu title editor wrote `en` for everyone regardless of the bot languages,
  // and a language can be removed in Bot Settings after texts were written.
  // Without this the form would show an empty field while the widget still
  // renders the orphaned value through its own fallback.
  const hasConfiguredTranslation = languages.some(
    (language) => translations[language],
  );

  if (!hasConfiguredTranslation) {
    const orphanedTranslation = Object.values(translations).find(
      (translation) => !!translation,
    );

    if (orphanedTranslation) {
      translations[legacyTextLanguage] = orphanedTranslation;
    }
  }

  for (const language of languages) {
    if (!translations[language]) {
      translations[language] = '';
    }
  }

  return translations;
};
